import React from 'react';
import { MessageCircle, Trash2, User } from 'lucide-react';
import type { Comment } from '../../types/comment';
import { useAuth } from '../../hooks/useAuth';

interface CommentListProps {
  comments: Comment[];
  onDeleteComment: (commentId: string) => Promise<void>;
}

export default function CommentList({ comments, onDeleteComment }: CommentListProps) {
  const { user } = useAuth();

  const handleDelete = async (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await onDeleteComment(commentId);
      } catch (error) {
        console.error('Failed to delete comment:', error);
      }
    }
  };

  const getDisplayName = (comment: Comment) => {
    if (comment.user_profile?.first_name || comment.user_profile?.last_name) {
      return `${comment.user_profile.first_name || ''} ${comment.user_profile.last_name || ''}`.trim();
    }
    return 'Anonymous Gardener';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (comments.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No comments yet. Be the first to share your thoughts!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-primary-500" />
        Comments ({comments.length})
      </h3>
      
      <div className="space-y-4">
        {comments.map((comment) => (
          <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">{getDisplayName(comment)}</p>
                  <p className="text-xs text-gray-500">{formatDate(comment.created_at)}</p>
                </div>
              </div>
              
              {user && user.id === comment.user_id && (
                <button
                  onClick={() => handleDelete(comment.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1"
                  title="Delete comment"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            
            <div className="text-gray-700 whitespace-pre-wrap">
              {comment.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}